import { useEffect, useId, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import { formatProfileTriggerName } from "../../utils/displayLabels";
import { getProfilePathForRole } from "../../utils/profileMenuPaths";
import "./HeaderProfileMenu.css";

function MenuIcon({ name }) {
  const common = { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.75" };
  if (name === "user") {
    return (
      <svg {...common} aria-hidden>
        <circle cx="12" cy="8" r="4" />
        <path d="M4 20c1.8-4.2 5-6 8-6s6.2 1.8 8 6" />
      </svg>
    );
  }
  if (name === "logout") {
    return (
      <svg {...common} aria-hidden>
        <path d="M9 4H6a2 2 0 00-2 2v12a2 2 0 002 2h3" />
        <path d="M16 17l5-5-5-5" />
        <path d="M21 12H9" />
      </svg>
    );
  }
  return null;
}

export default function HeaderProfileMenu({
  displayName = "User",
  roleLabel = "",
  initials = "U",
  avatarUrl = "",
  profilePath,
  authRole,
  className = "",
  triggerClassName = "",
  showCaret = true,
  compactName = false,
}) {
  const navigate = useNavigate();
  const menuId = useId();
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [avatarBroken, setAvatarBroken] = useState(false);

  const resolvedProfilePath =
    profilePath || getProfilePathForRole(authRole || api.getAuthRoleFromToken?.());
  const label = formatProfileTriggerName(displayName);

  useEffect(() => {
    setAvatarBroken(false);
  }, [avatarUrl]);

  useEffect(() => {
    if (!open) return undefined;
    function onPointerDown(event) {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    }
    function onKeyDown(event) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  function handleLogout() {
    setOpen(false);
    api.logoutToWebsite();
  }

  function goProfile() {
    setOpen(false);
    navigate(resolvedProfilePath);
  }

  return (
    <div className={`header-profile-menu ${className}`.trim()} ref={rootRef}>
      <button
        type="button"
        className={`header-profile-menu__trigger ${triggerClassName}`.trim()}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="header-profile-menu__avatar" aria-hidden="true">
          {avatarUrl && !avatarBroken ? (
            <img src={avatarUrl} alt="" onError={() => setAvatarBroken(true)} />
          ) : (
            initials
          )}
        </span>
        <span className="header-profile-menu__text">
          {compactName ? (
            <>
              <strong title={label}>{label}</strong>
              {roleLabel ? <small>{roleLabel}</small> : null}
            </>
          ) : (
            <>
              Welcome <strong title={label}>{label}</strong>
              {roleLabel ? <small>{roleLabel}</small> : null}
            </>
          )}
        </span>
        {showCaret ? (
          <span className="header-profile-menu__caret" aria-hidden="true">
            ▾
          </span>
        ) : null}
      </button>

      {open ? (
        <div className="header-profile-menu__panel" id={menuId} role="menu">
          <button type="button" className="header-profile-menu__item" role="menuitem" onClick={goProfile}>
            <MenuIcon name="user" />
            <span>Profile</span>
          </button>
          <button type="button" className="header-profile-menu__item header-profile-menu__item--logout" role="menuitem" onClick={handleLogout}>
            <MenuIcon name="logout" />
            <span>Logout</span>
          </button>
        </div>
      ) : null}
    </div>
  );
}
